/**
 * MiniLazyload
 * Copyright (c) Simon Raichl 2019 - 2020
 * MIT License
 */

/* jshint esversion: 6 */

function useNativeLazyload(miniLazyload, callback) {
  miniLazyload.allElements().forEach((element) => {
    if (element.dataset.bg || element.dataset.lazyClass) {
      miniLazyload.newObserver().observe(element);
    }
  });

  miniLazyload.loadImages((image) => {
    image.loading = 'lazy';
    if (callback) {
      callback(image);
    }
  });
}

// eslint-disable-next-line default-param-last
export default function MiniLazyload(options = {}, selector, override) {
  const translateSrcset = (element) => {
    if (window.HTMLPictureElement && element instanceof HTMLPictureElement) {
      [].slice.call(element.querySelectorAll('[data-srcset]')).forEach((source) => {
        source.srcset = source.dataset.srcset;
      });
    }
  };

  const loadImage = (target, loadSpecialAttrs = !window.IntersectionObserver) => {
    const { src, srcset, bg, lazyClass } = target.dataset;

    if (src) {
      target.src = src;
    }

    if (srcset) {
      target.srcset = srcset;
    }

    if (loadSpecialAttrs && bg) {
      target.style.backgroundImage = `url("${bg}")`;
    }

    if (loadSpecialAttrs && lazyClass) {
      target.classList.add(lazyClass);
    }

    translateSrcset(target.parentElement);
  };

  const onEvents = (element) => {
    const { placeholder, onload = () => {} } = this.options;
    const loaded = () => {
      onload(element);
      element.classList.add('loaded');
    };

    element.addEventListener('error', () => {
      if (placeholder && !element.classList.contains('error')) {
        element.src = placeholder;
      }
      element.classList.add('error');
      element.removeEventListener('load', loaded);
    });

    element.addEventListener('load', loaded);
  };

  this.loadImages = (callback = () => {}, loadImmediately = true) => {
    const intersectionObserver = window.IntersectionObserver && this.newObserver();

    this.allElements().forEach((element) => {
      onEvents(element);
      callback(element);

      if (!intersectionObserver || loadImmediately) {
        loadImage(element);
      } else {
        intersectionObserver.observe(element);
      }
    });
  };

  this.update = () => {
    if (this.enabled) {
      this.loadImages(() => {}, false);
    } else {
      useNativeLazyload(this);
    }
  };

  this.allElements = () => [].slice.call(document.querySelectorAll(this.selector));

  this.newObserver = () =>
    new IntersectionObserver((entries, observer) => {
      entries.forEach(({ intersectionRatio, target }) => {
        if (intersectionRatio > 0) {
          observer.unobserve(target);
          loadImage(target, true);
        }
      });
    }, this.options);

  this.selector = selector || '[loading=lazy]';
  this.options = options;
  this.enabled = !HTMLImageElement.loading || override === MiniLazyload.IGNORE_NATIVE_LAZYLOAD;
  this.update();
}

MiniLazyload.IGNORE_NATIVE_LAZYLOAD = true;
